import { LogLevel, LogService } from "./logging";
import {
  initiateOrderSync,
  initiateAnswerSync,
  initiatePaymentSync,
  initiateReceiptCountersSync,
  initiateTransactionsSync,
  resetAllSyncLocks,
} from "@/services/sync-service";
import { dexieDB } from "./dexie-database";
import { SyncStatus } from "@/types/db/product-slot-booking";
import { purgeExpiredTimeslots } from "@/dexie/timeslots";
import { checkAndRunMigrations } from "@/services/migration";

let _setupServicesCallCount = 0;
const MAX_SETUP_RETRIES = 2;

export async function setupServices() {
  _setupServicesCallCount++;
  if (_setupServicesCallCount > MAX_SETUP_RETRIES + 1) {
    console.error("setupServices: max retries exceeded, giving up");
    return;
  }

  // Ensure Dexie DB is open before using it
  try {
    if (!dexieDB.isOpen()) {
      // Race open() against a 10-second timeout to prevent indefinite hangs
      const openPromise = dexieDB.open();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error("Dexie open() timed out after 10s")),
          10000
        )
      );
      try {
        await Promise.race([openPromise, timeoutPromise]);
      } catch (raceErr: any) {
        // If timed out, try closing and re-opening once
        if (raceErr?.message?.includes("timed out")) {
          try {
            dexieDB.close();
          } catch (_) {
            // Log or ignore error intentionally: closing may fail if not open
          }
          await dexieDB.open();
        } else {
          throw raceErr;
        }
      }
    }
    // clean up stale orders and transactions (stuck in InProgress from crash)
    await resetStaleOrders();
    await resetStaleAnswerSyncs();
    await resetStaleTransactions();
    // purge timeslots that ended before today to prevent unbounded accumulation
    await purgeExpiredTimeslots();
  } catch (error) {
    LogService.info("Error during Dexie DB setup", error, LogLevel.Error);
  }

  // Run any pending data migrations
  await checkAndRunMigrations();

  // Setup the sync job triggers
  setupSyncTriggers();
}

/**
 * Function to setup sync triggers
 * This function is called once on app startup
 */
function setupSyncTriggers() {
  // Reset any stale sync locks from previous session (e.g., crash while syncing)
  resetAllSyncLocks();

  const syncIntervalMs = 2 * 60 * 1000;
  setInterval(initiateOrderSync, syncIntervalMs);
  setInterval(initiateAnswerSync, syncIntervalMs);
  setInterval(initiatePaymentSync, syncIntervalMs);
  setInterval(initiateReceiptCountersSync, syncIntervalMs);
  setInterval(initiateTransactionsSync, syncIntervalMs);

  // Trigger all sync jobs when the app is back online
  window.addEventListener("online", initiateOrderSync);
  window.addEventListener("online", initiateAnswerSync);
  window.addEventListener("online", initiatePaymentSync);
  window.addEventListener("online", initiateReceiptCountersSync);
  window.addEventListener("online", initiateTransactionsSync);
}

/**
 * Reset any orders stuck in InProgress sync state back to Pending
 * This handles crashes or force-closes while syncing
 */
async function resetStaleOrders() {
  try {
    // Double-check database is open
    if (!dexieDB.isOpen()) {
      LogService.info("Dexie DB not open, skipping stale order reset");
      return;
    }

    const staleOrders = await dexieDB.orders
      .where("syncStatus")
      .equals(SyncStatus.InProgress)
      .toArray();

    if (staleOrders.length) {
      LogService.info(`Found ${staleOrders.length} stale orders`);
    }

    for (const order of staleOrders) {
      LogService.info(`Sync: Going to mark order ${order._id} back to Pending`);
      try {
        // Field-level update — never overwrite the row payload with a stale copy.
        await dexieDB.orders.update(order._id, {
          syncStatus: SyncStatus.Pending,
        });
      } catch (error) {
        LogService.info(
          `Sync: Error marking order ${order._id} back to Pending`,
          error,
          LogLevel.Error
        );
      }
    }
  } catch (error) {
    LogService.info("Error resetting stale orders", error, LogLevel.Error);
  }
}

/**
 * Reset any answer-syncs stuck in InProgress back to Pending (crash/force-close).
 */
async function resetStaleAnswerSyncs() {
  try {
    if (!dexieDB.isOpen()) return;

    const stale = await dexieDB.customFieldAnswerSyncs
      .where("syncStatus")
      .equals(SyncStatus.InProgress)
      .toArray();

    if (!stale.length) return;

    LogService.info(
      `Found ${stale.length} stale answer syncs, resetting to Pending`
    );

    for (const record of stale) {
      try {
        // Field-level update — never overwrite the row payload with a stale copy.
        await dexieDB.customFieldAnswerSyncs.update(record._id, {
          syncStatus: SyncStatus.Pending,
        });
      } catch (error) {
        LogService.info(
          `Sync: Error marking answer sync ${record._id} back to Pending`,
          error,
          LogLevel.Error
        );
      }
    }
  } catch (error) {
    LogService.info(
      "Error resetting stale answer syncs",
      error,
      LogLevel.Error
    );
  }
}

/**
 * Reset any transactions stuck in InProgress sync state back to Pending
 * This handles crashes or force-closes while syncing
 */
async function resetStaleTransactions() {
  try {
    if (!dexieDB.isOpen()) return;

    const staleTransactions = await dexieDB.untypedTransactions
      .where("syncStatus")
      .equals(SyncStatus.InProgress)
      .toArray();

    if (!staleTransactions.length) return;

    LogService.info(
      `Found ${staleTransactions.length} stale transactions, resetting to Pending`
    );

    for (const tx of staleTransactions) {
      try {
        tx.syncStatus = SyncStatus.Pending;
        await dexieDB.untypedTransactions.put(tx);
      } catch (error) {
        LogService.info(
          `Sync: Error marking transaction ${tx.uuid} back to Pending`,
          error,
          LogLevel.Error
        );
      }
    }
  } catch (error) {
    LogService.info(
      "Error resetting stale transactions",
      error,
      LogLevel.Error
    );
  }
}
